import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement, BeamElement, BIMElement } from '../../core/types';
import { transformSceneObjects, copySceneObjects } from '../../core/scene/editRouter';
import { rotateZMatrix } from '../cad/entityGeometry';

/**
 * Rotate Tool - BIM 2025 Style
 * 
 * Rotates selected elements around a center point.
 * Supports:
 * - Custom rotation center
 * - Copy mode (Ctrl key)
 * - Precise angle input
 * - Constraint to 15° increments (Shift key)
 * 
 * Workflow:
 * 1. Select elements (or pre-select)
 * 2. Click center of rotation
 * 3. Click start angle reference
 * 4. Click end angle (or type angle)
 */
export class RotateToolClass extends BaseTool {
    id = 'ROTATE';
    name = 'Rotate';
    cursor = 'crosshair';

    private centerPoint: THREE.Vector3 | null = null;
    private startAnglePoint: THREE.Vector3 | null = null;
    private currentAnglePoint: THREE.Vector3 | null = null;
    private copyMode: boolean = false;
    private constrainToAngles: boolean = false;
    private selectedElements: BIMElement[] = [];
    // B4 cross-workbench: selection ids owned by OTHER workbenches.
    private foreignIds: string[] = [];

    activate(context: any): void {
        super.activate(context);
        this.loadSelection();

        if (this.selectedElements.length === 0) {
            console.log('Rotate: No elements selected. Click to select elements first.');
        } else {
            console.log('Rotate: Selected', this.selectedElements.length, 'elements. Click center of rotation.');
        }
    }

    private loadSelection(): void {
        if (!this.context) return;

        const selectedIds = this.context.appStore.selectedIds || [];
        this.selectedElements = [];
        this.foreignIds = [];

        for (const id of selectedIds) {
            const element = this.context.bimStore.project.elements.get(id);
            if (element) {
                this.selectedElements.push(element);
            } else {
                this.foreignIds.push(id);
            }
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        // If no selection, try to select clicked element
        if (this.selectedElements.length === 0) {
            const id = this.findElementIdAtPoint(event);
            if (id) {
                this.selectElement(id);
                this.loadSelection();
            }
            return;
        }

        if (!this.centerPoint) {
            // First click: Center of rotation
            this.centerPoint = snappedPoint.clone();
            console.log('Rotate: Center point set', this.centerPoint);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                center: this.centerPoint
            });
        } else if (!this.startAnglePoint) {
            // Second click: Start angle reference
            this.startAnglePoint = snappedPoint.clone();
            console.log('Rotate: Start angle reference set');

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                center: this.centerPoint,
                startAngle: this.startAnglePoint
            });
        } else {
            // Third click: End angle
            const endPoint = snappedPoint.clone();
            const angle = this.calculateAngle(endPoint);

            if (this.copyMode) {
                this.copyElements(angle);
            } else {
                this.rotateElements(angle);
            }

            // Reset and exit
            this.reset();
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.centerPoint) return;

        const point = this.getIntersection(event);
        if (!point) return;

        this.currentAnglePoint = this.snapToGrid(point);

        if (this.startAnglePoint) {
            const previewAngle = this.calculateAngle(this.currentAnglePoint);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                center: this.centerPoint,
                startAngle: this.startAnglePoint,
                currentAngle: this.currentAnglePoint,
                angle: previewAngle,
                copyMode: this.copyMode
            });
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        // Toggle copy mode
        if (event.ctrlKey) {
            this.copyMode = true;
            console.log('Rotate: Copy mode ON');
        }

        // Constrain to 15° increments with Shift
        if (event.shiftKey) {
            this.constrainToAngles = true;
        }

        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private calculateAngle(endPoint: THREE.Vector3): number {
        if (!this.centerPoint || !this.startAnglePoint) return 0;

        // Calculate angles from center
        const startVec = this.startAnglePoint.clone().sub(this.centerPoint);
        const endVec = endPoint.clone().sub(this.centerPoint);

        // Calculate angles in XY plane
        const startAngle = Math.atan2(startVec.y, startVec.x);
        const endAngle = Math.atan2(endVec.y, endVec.x);

        const angle = endAngle - startAngle;

        // Convert to degrees
        let angleDeg = THREE.MathUtils.radToDeg(angle);

        // Constrain to 15° increments if shift is held
        if (this.constrainToAngles) {
            angleDeg = Math.round(angleDeg / 15) * 15;
        }

        return angleDeg;
    }

    private rotateElements(angleDeg: number): void {
        if (!this.context || !this.centerPoint) return;

        for (const element of this.selectedElements) {
            this.rotateElement(element, angleDeg, false);
        }

        // B4 cross-workbench: foreign citizens rotate through the edit router.
        if (this.foreignIds.length > 0) {
            transformSceneObjects(
                this.foreignIds,
                rotateZMatrix([this.centerPoint.x, this.centerPoint.y], THREE.MathUtils.degToRad(angleDeg)),
                'Rotate'
            );
        }

        console.log('Rotate: Rotated', this.selectedElements.length + this.foreignIds.length, 'objects by', angleDeg, 'degrees');
    }

    private copyElements(angleDeg: number): void {
        if (!this.context || !this.centerPoint) return;

        const newIds: string[] = [];

        for (const element of this.selectedElements) {
            const newId = this.rotateElement(element, angleDeg, true);
            if (newId) newIds.push(newId);
        }

        // B4 cross-workbench: foreign citizens copy-rotate through the router.
        if (this.foreignIds.length > 0) {
            newIds.push(...copySceneObjects(
                this.foreignIds,
                rotateZMatrix([this.centerPoint.x, this.centerPoint.y], THREE.MathUtils.degToRad(angleDeg)),
                'Rotate Copy'
            ));
        }

        // Select the new copies
        this.context.appStore.select(newIds);
        console.log('Rotate: Created', newIds.length, 'rotated copies');
    }

    private rotateElement(element: BIMElement, angleDeg: number, copy: boolean): string | null {
        if (!this.context || !this.centerPoint) return null;

        const angleRad = THREE.MathUtils.degToRad(angleDeg);

        switch (element.category) {
            case 'Walls':
                return this.rotateWall(element as WallElement, angleRad, copy);
            case 'Structural Framing':
                return this.rotateBeam(element as BeamElement, angleRad, copy);
            default:
                return this.rotateGenericElement(element, angleRad, copy);
        }
    }

    private rotatePoint(point: THREE.Vector3, center: THREE.Vector3, angleRad: number): THREE.Vector3 {
        // Translate to origin
        const p = point.clone().sub(center);

        // Rotate in XY plane
        const cos = Math.cos(angleRad);
        const sin = Math.sin(angleRad);
        const newX = p.x * cos - p.y * sin;
        const newY = p.x * sin + p.y * cos;

        // Translate back
        return new THREE.Vector3(newX + center.x, newY + center.y, point.z);
    }

    private rotateWall(wall: WallElement, angleRad: number, copy: boolean): string | null {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);

        const newStart = this.rotatePoint(start, this.centerPoint!, angleRad);
        const newEnd = this.rotatePoint(end, this.centerPoint!, angleRad);

        const newGeometry = {
            ...wall.geometryData,
            start: [newStart.x, newStart.y, newStart.z] as [number, number, number],
            end: [newEnd.x, newEnd.y, newEnd.z] as [number, number, number]
        };

        if (copy) {
            const newWall: WallElement = {
                ...wall,
                id: THREE.MathUtils.generateUUID(),
                geometryData: newGeometry,
                constraints: []
            };
            this.context!.bimStore.addElement(newWall);
            return newWall.id;
        } else {
            this.context!.bimStore.updateElement(wall.id, { geometryData: newGeometry });
            return wall.id;
        }
    }

    private rotateBeam(beam: BeamElement, angleRad: number, copy: boolean): string | null {
        const start = new THREE.Vector3(...beam.geometryData.start);
        const end = new THREE.Vector3(...beam.geometryData.end);

        const newStart = this.rotatePoint(start, this.centerPoint!, angleRad);
        const newEnd = this.rotatePoint(end, this.centerPoint!, angleRad);

        const newGeometry = {
            ...beam.geometryData,
            start: [newStart.x, newStart.y, newStart.z] as [number, number, number],
            end: [newEnd.x, newEnd.y, newEnd.z] as [number, number, number],
            rotation: beam.geometryData.rotation + THREE.MathUtils.radToDeg(angleRad)
        };

        if (copy) {
            const newBeam: BeamElement = {
                ...beam,
                id: THREE.MathUtils.generateUUID(),
                geometryData: newGeometry,
                constraints: []
            };
            this.context!.bimStore.addElement(newBeam);
            return newBeam.id;
        } else {
            this.context!.bimStore.updateElement(beam.id, { geometryData: newGeometry });
            return beam.id;
        }
    }

    private rotateGenericElement(element: BIMElement, _angleRad: number, copy: boolean): string | null {
        // For generic elements without specific geometry data, just create a copy
        if (copy) {
            const newElement = {
                ...element,
                id: THREE.MathUtils.generateUUID(),
                constraints: []
            };
            this.context!.bimStore.addElement(newElement);
            return newElement.id;
        }
        return element.id;
    }

    private reset(): void {
        this.centerPoint = null;
        this.startAnglePoint = null;
        this.currentAnglePoint = null;
        this.copyMode = false;
        this.constrainToAngles = false;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
        this.selectedElements = [];
    }
}
